import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined } from '@ant-design/icons';
import '../styles/Address.css';
import { GET_CUSTOMER_ADDRESSES, DELETE_CUSTOMER_ADDRESS, GET_DELIVERY_CHARGES } from '../graphql/queries';
import { submitOrderWithAddress } from '../services/orderService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useRestaurant } from '../context/RestaurantContext';
import { useCart } from '../context/CartContext';
import { toPoint, isInsidePolygon, Polygon, GeoPoint } from '../utils/geo';
import { formatPrice } from '../config';

type AddressEvaluation = {
  address: any;
  point?: GeoPoint;
  isDeliverable: boolean;
  reason?: string;
};

const AddressSelection: React.FC = () => {
  const navigate = useNavigate();
  const { restaurantId, token } = useParams<{ restaurantId: string; token: string }>();
  const location = useLocation();
  const { handleError, clearError } = useErrorHandler();
  const [addresses, setAddresses] = useState<any[]>([]);
  const [pendingOrder, setPendingOrder] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<{ evaluation: AddressEvaluation; point: GeoPoint } | null>(null);
  const selectedAddressIdRef = useRef<string | null>(null);
  const [deliveryCharge, setDeliveryCharge] = useState<number | null>(null);
  const [isFetchingDeliveryCharge, setIsFetchingDeliveryCharge] = useState(false);
  const [isPickupSelected, setIsPickupSelected] = useState(false);
  const {
    restaurantData,
    loadRestaurant,
    currentRestaurantId,
    isLoading: restaurantLoading,
    error: restaurantError,
  } = useRestaurant();
  const { cart, option_map } = useCart();

  useEffect(() => {
    if (restaurantId && currentRestaurantId !== restaurantId) {
      loadRestaurant(restaurantId).catch(error => {
        const messageText = error instanceof Error ? error.message : 'Failed to load restaurant data';
        handleError(messageText);
        navigate('/error', {
          state: {
            error: messageText,
            title: 'Restaurant Error',
          },
        });
      });
    }
  }, [restaurantId, currentRestaurantId, loadRestaurant, handleError, navigate]);

  useEffect(() => {
    if (restaurantError) {
      handleError(restaurantError);
    }
  }, [restaurantError, handleError]);

  // Query addresses
  const { loading, data, refetch } = useQuery(GET_CUSTOMER_ADDRESSES, {
    variables: {
      limit: 20,
    },
    fetchPolicy: 'network-only', // Always fetch from network
    onError: error => {
      handleError(error.message);
      navigate('/error', {
        state: {
          error: error.message,
          title: 'Error Loading Addresses',
        },
      });
    },
  });

  // Delete address mutation
  const [deleteAddress] = useMutation(DELETE_CUSTOMER_ADDRESS, {
    onError: error => {
      handleError(error.message);
      navigate('/error', {
        state: {
          error: error.message,
          title: 'Error Deleting Address',
        },
      });
    },
    onCompleted: () => {
      message.success('Address deleted successfully');
      clearError();
      refetch();
    },
  });

  const [fetchDeliveryCharges, { loading: deliveryChargeLoading }] = useLazyQuery(GET_DELIVERY_CHARGES, {
    fetchPolicy: 'network-only',
  });

  // Get pending order from sessionStorage
  useEffect(() => {
    const orderData = sessionStorage.getItem('pendingOrder');
    if (orderData) {
      try {
        setPendingOrder(JSON.parse(orderData));
      } catch (e) {
        console.error('Error parsing pending order:', e);
        handleError('Invalid order data');
        navigate('/error', {
          state: {
            error: 'Invalid order data. Please try again.',
            title: 'Order Error',
          },
        });
      }
    }
  }, [handleError]);

  // Update addresses list when data changes
  useEffect(() => {
    if (data?.customerAddresses) {
      setAddresses(data.customerAddresses);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedAddress) {
      return;
    }

    const exists = addresses.some(addr => addr.addressId === selectedAddress.evaluation.address.addressId);
    if (!exists) {
      setSelectedAddress(null);
      setDeliveryCharge(null);
      selectedAddressIdRef.current = null;
      setIsPickupSelected(false);
    }
  }, [addresses, selectedAddress]);

  const customerIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('d') ?? undefined;
  }, [location.search]);

  const activeCustomerId = pendingOrder?.customerId ?? customerIdFromQuery;
  const itemsSubtotal = useMemo(() => Number(pendingOrder?.itemsSubtotal ?? 0), [pendingOrder]);
  const tipping = useMemo(() => Number(pendingOrder?.tipping ?? 0), [pendingOrder]);
  const taxationAmount = useMemo(() => Number(pendingOrder?.taxationAmount ?? 0), [pendingOrder]);
  const baseOrderTotal = useMemo(
    () => Number((itemsSubtotal + tipping + taxationAmount).toFixed(2)),
    [itemsSubtotal, tipping, taxationAmount]
  );
  const getOptionMetaById = useCallback(
    (optionId: string) => {
      const optionFromMap = option_map?.[optionId];
      if (optionFromMap) {
        return optionFromMap;
      }

      const fallback = restaurantData?.options?.find((opt: any) => String(opt._id) === String(optionId)) ?? undefined;
      if (fallback) {
        return {
          _id: fallback._id,
          title: fallback.title,
          price: Number(fallback.price ?? 0),
        };
      }
      return undefined;
    },
    [option_map, restaurantData]
  );

  const calculateItemPrice = useCallback(
    (item: any) => {
      const basePrice = Number(item?.variation?.price ?? item?.menuItem?.Price ?? 0);
      let addonsPrice = 0;

      item?.selectedOptions?.forEach((selectedOption: any) => {
        selectedOption?.optionIds?.forEach((optionId: string) => {
          const optionMeta = getOptionMetaById(optionId);
          if (optionMeta) {
            addonsPrice += Number(optionMeta.price ?? 0);
          }
        });
      });

      return (basePrice + addonsPrice) * Number(item?.quantity ?? 0);
    },
    [getOptionMetaById]
  );

  const getOptionTitles = useCallback(
    (item: any) => {
      const titles: string[] = [];
      item?.selectedOptions?.forEach((selectedOption: any) => {
        selectedOption?.optionIds?.forEach((optionId: string) => {
          const optionMeta = getOptionMetaById(optionId);
          if (optionMeta?.title) {
            titles.push(optionMeta.title);
          }
        });
      });
      return titles;
    },
    [getOptionMetaById]
  );

  const findMenuItemById = useCallback(
    (foodId: string) => {
      if (!restaurantData?.categories) {
        return undefined;
      }

      for (const category of restaurantData.categories) {
        const match = category?.foods?.find((food: any) => String(food._id) === String(foodId));
        if (match) {
          return match;
        }
      }

      return undefined;
    },
    [restaurantData]
  );

  const fallbackCartItems = useMemo(() => {
    if (!pendingOrder?.orderInput || !Array.isArray(pendingOrder.orderInput)) {
      return [];
    }

    return pendingOrder.orderInput
      .map((orderItem: any, index: number) => {
        const menuItemData = findMenuItemById(orderItem.food);
        if (!menuItemData) {
          return null;
        }

        const menuItem = {
          MenuItemId: menuItemData._id,
          Name: menuItemData.title,
          Price: Number(menuItemData.price ?? 0),
        };

        let variation;
        if (orderItem.variation && Array.isArray(menuItemData.variations)) {
          const variationData = menuItemData.variations.find(
            (variant: any) => String(variant._id) === String(orderItem.variation)
          );

          if (variationData) {
            variation = {
              _id: variationData._id,
              title: variationData.title,
              price: Number(variationData.price ?? variationData.discounted ?? menuItemData.price ?? 0),
            };
          }
        }

        const selectedOptions =
          orderItem.addons?.map((addon: any) => ({
            optionSetId: addon._id,
            optionIds: addon.options ?? [],
          })) ?? [];

        return {
          cartItemId: `pending-${index}`,
          menuItem,
          quantity: Number(orderItem.quantity ?? 0),
          selectedOptions,
          variation,
        };
      })
      .filter(Boolean);
  }, [pendingOrder, findMenuItemById]);

  const summaryItems = useMemo(() => {
    const sourceItems = (cart.length > 0 ? cart : fallbackCartItems) as any[];
    return sourceItems
      .map(item => {
        const quantity = Number(item?.quantity ?? 0);
        if (quantity <= 0) {
          return null;
        }

        const name = item?.menuItem?.Name ?? 'Item';
        const label = `${quantity}x ${name}`;

        return {
          key: item?.cartItemId ?? `${name}-${quantity}-${Math.random().toString(36).slice(2)}`,
          label,
          options: getOptionTitles(item),
          totalPrice: calculateItemPrice(item),
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      label: string;
      options: string[];
      totalPrice: number;
    }>;
  }, [cart, fallbackCartItems, calculateItemPrice, getOptionTitles]);

  const totalWithDelivery = useMemo(() => {
    if (!selectedAddress || deliveryCharge === null) {
      return null;
    }
    return Number((itemsSubtotal + tipping + taxationAmount + deliveryCharge).toFixed(2));
  }, [selectedAddress, deliveryCharge, itemsSubtotal, tipping, taxationAmount]);

  const deliveryFeeDisplay = isPickupSelected
    ? formatPrice(0)
    : selectedAddress
    ? deliveryCharge !== null
      ? formatPrice(deliveryCharge)
      : 'Calculating...'
    : '--';
  const totalDisplay = isPickupSelected
    ? formatPrice(baseOrderTotal)
    : selectedAddress
    ? deliveryCharge !== null && totalWithDelivery !== null
      ? formatPrice(totalWithDelivery)
      : 'Calculating...'
    : formatPrice(baseOrderTotal);
  const canConfirmDelivery = isPickupSelected
    ? !isSubmitting
    : Boolean(
        selectedAddress && deliveryCharge !== null && !isFetchingDeliveryCharge && !deliveryChargeLoading && !isSubmitting
      );
  const deliveryAddressDisplay = isPickupSelected
    ? 'Self pickup at restaurant'
    : selectedAddress
    ? selectedAddress.evaluation.address.formattedAddress ??
      [
        selectedAddress.evaluation.address.streetAddress,
        selectedAddress.evaluation.address.city,
        selectedAddress.evaluation.address.state,
        selectedAddress.evaluation.address.postcode,
      ]
        .filter(Boolean)
        .join(', ')
    : 'Select an address';
  const restaurantName = restaurantData?.name ?? 'Unknown restaurant';

  const deliveryBounds = useMemo<Polygon | undefined>(() => {
    if (!restaurantData?.deliveryBounds || restaurantData.deliveryBounds.length === 0) {
      return undefined;
    }
    return restaurantData.deliveryBounds as unknown as Polygon;
  }, [restaurantData]);

  const evaluatedAddresses = useMemo<AddressEvaluation[]>(() => {
    return addresses.map(address => {
      let point: GeoPoint | undefined;
      let isDeliverable = true;
      let reason: string | undefined;

      try {
        point = toPoint(address.coordinates ?? {});
      } catch (error) {
        isDeliverable = false;
        reason = 'This address is missing valid coordinates.';
      }

      if (isDeliverable && point && deliveryBounds && deliveryBounds.length > 0) {
        const inside = isInsidePolygon(point.coordinates, deliveryBounds);
        if (!inside) {
          isDeliverable = false;
          reason = 'We cannot deliver to this address.';
        }
      }

      if (!restaurantData) {
        return {
          address,
          point,
          isDeliverable: false,
          reason: reason ?? 'Loading restaurant information...',
        };
      }

      return {
        address,
        point,
        isDeliverable,
        reason,
      };
    });
  }, [addresses, deliveryBounds, restaurantData]);

  // Add new address
  const handleAddAddress = () => {
    if (restaurantId && token) {
      navigate(`/${restaurantId}/a/${token}/add`);
    } else {
      console.error('Missing restaurantId or token for navigation');
      navigate('/error', {
        state: {
          error: 'Missing restaurant ID or token',
          title: 'Navigation Error',
        },
      });
    }
  };

  // Edit address
  const handleEditAddress = (address: any) => {
    if (restaurantId && token) {
      navigate(`/${restaurantId}/a/${token}/edit/${address.addressId}`, {
        state: { address },
      });
    } else {
      console.error('Missing restaurantId or token for navigation');
      navigate('/error', {
        state: {
          error: 'Missing restaurant ID or token',
          title: 'Navigation Error',
        },
      });
    }
  };

  // Delete address
  const handleDeleteAddress = async (addressId: string) => {
    Modal.confirm({
      title: 'Delete Address',
      content: 'Are you sure you want to delete this address?',
      onOk: async () => {
        try {
          if (selectedAddress?.evaluation.address.addressId === addressId) {
            setSelectedAddress(null);
            setDeliveryCharge(null);
            selectedAddressIdRef.current = null;
            setIsPickupSelected(false);
          }
          await deleteAddress({
            variables: {
              addressId,
            },
          });
          // Success is handled in onCompleted callback
        } catch (error) {
          // Error is already handled in the mutation's onError callback
          console.error('Delete address error:', error);
        }
      },
    });
  };

  // Select address and load delivery charges
  const handleSelectAddress = async ({ address, point, isDeliverable, reason }: AddressEvaluation) => {
    if (!pendingOrder) {
      handleError('No order information found. Please try again.');
      navigate('/error', {
        state: {
          error: 'No order information found. Please try again.',
          title: 'Order Error',
        },
      });
      return;
    }

    if (!restaurantData) {
      message.error('Restaurant information is unavailable. Please try again later.');
      return;
    }

    if (!address?.addressId) {
      message.error('This address is missing required information.');
      return;
    }

    if (!isDeliverable) {
      message.warning(reason ?? 'We cannot deliver to this address.');
      return;
    }

    let resolvedPoint = point as GeoPoint | undefined;
    try {
      if (!resolvedPoint) {
        resolvedPoint = toPoint(address.coordinates ?? {});
      }
    } catch (error) {
      message.error('This address is missing valid coordinates. Please edit it or choose another one.');
      return;
    }

    if (!resolvedPoint) {
      message.error('This address is missing valid coordinates. Please edit it or choose another one.');
      return;
    }

    const bounds = deliveryBounds;
    if (bounds && bounds.length > 0) {
      const inside = isInsidePolygon(resolvedPoint.coordinates, bounds);
      if (!inside) {
        message.error('We cannot deliver to this address.');
        return;
      }
    }

    if (!pendingOrder.restaurantId) {
      message.error('Missing restaurant information for delivery charges.');
      return;
    }

    setIsPickupSelected(false);
    selectedAddressIdRef.current = address.addressId;
    setSelectedAddress({ evaluation: { address, point: resolvedPoint, isDeliverable, reason }, point: resolvedPoint });
    setDeliveryCharge(null);
    setIsFetchingDeliveryCharge(true);

    try {
      const { data } = await fetchDeliveryCharges({
        variables: {
          customerId: activeCustomerId ?? null,
          restaurantId: pendingOrder.restaurantId,
          addressId: address.addressId,
        },
      });

      if (selectedAddressIdRef.current !== address.addressId) {
        return;
      }

      const fee = Number(data?.getDeliveryCharges?.deliveryCharges ?? 0);
      if (!Number.isFinite(fee)) {
        throw new Error('Invalid delivery fee received from server.');
      }
      setDeliveryCharge(Number(fee.toFixed(2)));
    } catch (error) {
      if (selectedAddressIdRef.current === address.addressId) {
        selectedAddressIdRef.current = null;
        setSelectedAddress(null);
        setDeliveryCharge(null);
        setIsPickupSelected(false);
      }
      console.error('Failed to fetch delivery charges:', error);
      message.error('Unable to retrieve delivery charges. Please try again.');
    } finally {
      if (selectedAddressIdRef.current === address.addressId || selectedAddressIdRef.current === null) {
        setIsFetchingDeliveryCharge(false);
      }
    }
  };

  const handleConfirmDelivery = async () => {
    if (!pendingOrder) {
      handleError('No order information found. Please try again.');
      navigate('/error', {
        state: {
          error: 'No order information found. Please try again.',
          title: 'Order Error',
        },
      });
      return;
    }

    if (isPickupSelected) {
      const orderAmount = Number((itemsSubtotal + tipping + taxationAmount).toFixed(2));

      setIsSubmitting(true);
      try {
        await submitOrderWithAddress({
          ...pendingOrder,
          deliveryAddress: undefined,
          deliveryAddressId: undefined,
          deliveryCharges: 0,
          itemsSubtotal,
          tipping,
          taxationAmount,
          orderAmount,
          isPickedUp: true,
        });

        sessionStorage.removeItem('pendingOrder');
        clearError();
        navigate('/success');
      } catch (error) {
        console.error('Error submitting pickup order:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to place pickup order. Please try again.';
        handleError(errorMessage);
        navigate('/error', {
          state: {
            error: errorMessage,
            title: 'Pickup Order Error',
          },
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!selectedAddress) {
      message.warning('Please select a delivery address first.');
      return;
    }

    if (deliveryCharge === null) {
      message.error('Delivery charges are not available. Please re-select the address.');
      return;
    }

    const {
      evaluation: { address },
      point,
    } = selectedAddress;
    const orderAmount = Number((itemsSubtotal + deliveryCharge + tipping + taxationAmount).toFixed(2));

    const [longitude, latitude] = point.coordinates;

    const deliveryAddress = {
      addressId: address.addressId,
      placeId: address.placeId,
      formattedAddress: address.formattedAddress,
      streetAddress: address.streetAddress,
      city: address.city,
      state: address.state,
      postcode: address.postcode,
      country: address.country,
      recipientName: address.recipientName,
      phone: address.phone,
      tag: address.tag,
      coordinates: {
        longitude,
        latitude,
      },
    };

    setIsSubmitting(true);
    try {
      await submitOrderWithAddress({
        ...pendingOrder,
        deliveryAddressId: address.addressId,
        deliveryAddress,
        deliveryCharges: deliveryCharge,
        itemsSubtotal,
        tipping,
        taxationAmount,
        orderAmount,
        isPickedUp: false,
      });

      sessionStorage.removeItem('pendingOrder');
      clearError();
      navigate('/success');
    } catch (error) {
      console.error('Error submitting order:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to place order. Please try again.';
      const lowerMessage = errorMessage.toLowerCase();
      if (
        lowerMessage.includes('invalid coordinates') ||
        lowerMessage.includes('out of bounds') ||
        lowerMessage.includes('delivery area')
      ) {
        message.error('The selected address is invalid or outside the delivery area. Please choose another address.');
        clearError();
      } else {
        handleError(errorMessage);
        navigate('/error', {
          state: {
            error: errorMessage,
            title: 'Order Submission Error',
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle pickup option selection
  const handlePickupSelection = () => {
    if (!pendingOrder) {
      handleError('No order information found. Please try again.');
      navigate('/error', {
        state: {
          error: 'No order information found. Please try again.',
          title: 'Order Error',
        },
      });
      return;
    }

    selectedAddressIdRef.current = null;
    setSelectedAddress(null);
    setDeliveryCharge(0);
    setIsFetchingDeliveryCharge(false);
    setIsPickupSelected(true);
  };

  // Return to cart
  const handleBackToCart = () => {
    clearError();
    if (restaurantId && token) {
      navigate(`/${restaurantId}/${token}`);
    } else {
      navigate(-1); // Fallback to previous page
    }
  };

  if (loading || restaurantLoading) return <div>Loading...</div>;

  return (
    <div className="address-container">
      {!pendingOrder && (
        <div className="no-order-warning">
          <p>No order information found. Please return to cart and try again.</p>
          <Button type="primary" onClick={handleBackToCart}>
            Back to Cart
          </Button>
        </div>
      )}

      {pendingOrder && (
        <div className="address-panels">
          <section className="address-panel">
            <h3 className="summary-title">Select Delivery Method</h3>
            <div className="delivery-option-list">
              {evaluatedAddresses.length === 0 && (
                <p className="delivery-option-empty">No saved delivery addresses yet. Add a new address below.</p>
              )}
              <div
                className={`delivery-option delivery-option--pickup${isPickupSelected ? ' delivery-option--selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!isSubmitting) {
                    handlePickupSelection();
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!isSubmitting) {
                      handlePickupSelection();
                    }
                  }
                }}
              >
                <div className="delivery-option-content">
                  <span
                    className={`delivery-option-indicator${isPickupSelected ? ' delivery-option-indicator--active' : ''}`}
                    aria-hidden="true"
                  />
                  <div className="delivery-option-main">
                    <div className="delivery-option-top">
                      <span className="delivery-option-title">
                        <ShopOutlined />
                        <span>Self Pickup</span>
                      </span>
                    </div>
                    <div className="delivery-option-address">Collect your order from the restaurant.</div>
                  </div>
                </div>
              </div>

              {evaluatedAddresses.map(item => {
                const { address, isDeliverable, reason } = item;
                const isSelected = selectedAddress?.evaluation.address.addressId === address.addressId;
                const classes = ['delivery-option'];

                if (isSelected) {
                  classes.push('delivery-option--selected');
                }
                if (!isDeliverable) {
                  classes.push('delivery-option--disabled');
                }
                if (isSelected && (isFetchingDeliveryCharge || deliveryChargeLoading)) {
                  classes.push('delivery-option--loading');
                }

                const activateOption = () => {
                  handleSelectAddress(item);
                };

                const uniqueKey =
                  address.addressId ??
                  address.placeId ??
                  (address.formattedAddress ? `${address.formattedAddress}-${address.phone ?? ''}` : address.recipientName);

                return (
                  <div
                    key={uniqueKey}
                    className={classes.join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={activateOption}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        activateOption();
                      }
                    }}
                  >
                    <div className="delivery-option-content">
                      <span
                        className={`delivery-option-indicator${isSelected ? ' delivery-option-indicator--active' : ''}`}
                        aria-hidden="true"
                      />
                      <div className="delivery-option-main">
                        <div className="delivery-option-top">
                          <span className="delivery-option-title">
                            {address.recipientName}
                            {address.tag && <span className="delivery-option-tag">{address.tag}</span>}
                          </span>
                          <span className="delivery-option-phone">{address.phone}</span>
                        </div>
                        <div className="delivery-option-address">{address.formattedAddress}</div>
                        {reason && <div className="delivery-option-note">{reason}</div>}
                        {isSelected && (isFetchingDeliveryCharge || deliveryChargeLoading) && (
                          <div className="delivery-option-status">Calculating delivery fee…</div>
                        )}
                      </div>
                    </div>
                    <div className="delivery-option-actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={event => {
                          event.stopPropagation();
                          handleEditAddress(address);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={event => {
                          event.stopPropagation();
                          handleDeleteAddress(address.addressId);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}

              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddAddress} block className="delivery-option-add">
                Add New Address
              </Button>
            </div>
          </section>

          <section className="address-panel summary-panel">
            <h3 className="summary-title">Order Summary</h3>
            {summaryItems.length > 0 ? (
              <div className="summary-items">
                {summaryItems.map(item => (
                  <div key={item.key} className="summary-item">
                    <div className="summary-item-line">
                      <span className="summary-item-label">- {item.label}</span>
                      <span className="summary-item-value">{formatPrice(item.totalPrice)}</span>
                    </div>
                    {item.options.map(option => (
                      <div key={`${item.key}-${option}`} className="summary-item-option">
                        - {option}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className="summary-empty">No items in order.</p>
            )}

            <div className="summary-divider" />

            <div className="summary-totals">
              <div className="summary-row">
                <span className="summary-label"> Subtotal</span>
                <span className="summary-value">{formatPrice(itemsSubtotal)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label"> Delivery Fee</span>
                <span className="summary-value">{deliveryFeeDisplay}</span>
              </div>
              <div className="summary-row summary-row--total">
                <span className="summary-label"> Total</span>
                <span className="summary-value">{totalDisplay}</span>
              </div>
            </div>

            <div className="summary-divider" />

            <div className="summary-meta">
              <div className="summary-meta-line">🏪 Restaurant: {restaurantName}</div>
              <div className="summary-meta-line">📍 Delivery to: {deliveryAddressDisplay}</div>
            </div>

            <Button
              type="primary"
              onClick={handleConfirmDelivery}
              disabled={!canConfirmDelivery}
              loading={isSubmitting}
              className="primary-button"
              style={{ width: '100%' }}
            >
              Confirm Delivery
            </Button>
          </section>
        </div>
      )}

      <div className="address-footer">
        <Button onClick={handleBackToCart}>Back to Cart</Button>
      </div>
    </div>
  );
};

export default AddressSelection;
